import CommonUtils from './commonUtils';

export const SERVER_CHUNK_SIZE = 1024 * 1024;
export const SERVER_MAX_FILE_SIZE = 256 * 1024 * 1024;
const HEADER_SIZE = 36; // 32 ASCII binding bytes + uint32 chunk index
const OVERHEAD = HEADER_SIZE + 12 + 16; // AES-GCM IV and tag
export type ServerFileMetadata = {
    id: number;
    name: string;
    size: number;
    mimeType: string;
    binding: string;
    version: 1;
};

export function encryptedSize(size: number): number {
    return size + Math.max(1, Math.ceil(size / SERVER_CHUNK_SIZE)) * OVERHEAD;
}

/** A domain-separated capability, never the AES key itself. */
export async function serverCapability(key: CryptoKey): Promise<string> {
    const raw = await CommonUtils.exportKey(key);
    const digest = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(`qrfile-server-v1:${raw}`),
    );
    return Array.from(new Uint8Array(digest), (b) =>
        b.toString(16).padStart(2, '0'),
    ).join('');
}

export async function encryptServerFile(
    key: CryptoKey,
    file: File,
    id: number,
    checkCancelled: () => void,
): Promise<{ blob: Blob; metadata: string }> {
    if (file.size > SERVER_MAX_FILE_SIZE) {
        throw new Error(
            'This connection supports files up to 256 MB. Try a smaller file.',
        );
    }
    const binding = CommonUtils.randomBase62(32);
    const metadata: ServerFileMetadata = {
        version: 1,
        id,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        binding,
    };
    const parts: ArrayBuffer[] = [];
    for (
        let index = 0;
        index < Math.max(1, Math.ceil(file.size / SERVER_CHUNK_SIZE));
        index++
    ) {
        checkCancelled();
        const bytes = await file
            .slice(index * SERVER_CHUNK_SIZE, (index + 1) * SERVER_CHUNK_SIZE)
            .arrayBuffer();
        const frame = new Uint8Array(HEADER_SIZE + bytes.byteLength);
        frame.set(new TextEncoder().encode(binding));
        new DataView(frame.buffer).setUint32(32, index);
        frame.set(new Uint8Array(bytes), HEADER_SIZE);
        parts.push(await CommonUtils.encryptChunk(key, frame.buffer));
    }
    checkCancelled();
    return {
        blob: new Blob(parts, { type: 'application/octet-stream' }),
        metadata: await CommonUtils.encryptString(
            key,
            JSON.stringify(metadata),
        ),
    };
}

export async function readServerMetadata(
    key: CryptoKey,
    encrypted: string,
): Promise<ServerFileMetadata> {
    const m = JSON.parse(await CommonUtils.decryptString(key, encrypted));
    if (
        m.version !== 1 ||
        !Number.isInteger(m.id) ||
        m.id < 0 ||
        m.id > 0xffffffff ||
        typeof m.name !== 'string' ||
        m.name.length > 4096 ||
        typeof m.mimeType !== 'string' ||
        typeof m.binding !== 'string' ||
        !/^[a-zA-Z0-9]{32}$/.test(m.binding) ||
        !Number.isSafeInteger(m.size) ||
        m.size < 0 ||
        m.size > SERVER_MAX_FILE_SIZE
    ) {
        throw new Error(
            'Invalid encrypted file details. Please send the file again.',
        );
    }
    return m;
}

export async function decryptServerFile(
    key: CryptoKey,
    blob: Blob,
    metadata: ServerFileMetadata,
    onProgress: (bytes: number) => void,
    checkCancelled: () => void,
): Promise<File> {
    if (blob.size !== encryptedSize(metadata.size))
        throw new Error('Incomplete file. Please send it again.');
    const parts: ArrayBuffer[] = [];
    let received = 0;
    let offset = 0;
    for (
        let index = 0;
        index < Math.max(1, Math.ceil(metadata.size / SERVER_CHUNK_SIZE));
        index++
    ) {
        checkCancelled();
        const length =
            Math.min(SERVER_CHUNK_SIZE, metadata.size - received) + OVERHEAD;
        const frame = await CommonUtils.decryptChunk(
            key,
            await blob.slice(offset, offset + length).arrayBuffer(),
        );
        if (
            new TextDecoder().decode(frame.slice(0, 32)) !== metadata.binding ||
            new DataView(frame).getUint32(32) !== index
        )
            throw new Error('File verification failed. Please send it again.');
        const data = frame.slice(HEADER_SIZE);
        parts.push(data);
        received += data.byteLength;
        offset += length;
        onProgress(received);
    }
    checkCancelled();
    return new File(parts, metadata.name, { type: metadata.mimeType });
}
